import { useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { NotificationEntry, NotificationInbox, productNames } from '@beyond-js/ui/react';
import { useApi } from '@cdn/api';
import { useI18n } from '@cdn/i18n';
import { Paths } from '@cdn/navigation';
import { RelayAdapter } from './adapter';
import { PRODUCTS, useLabels } from './labels';

/**
 * Where an opened notification leads. A destination of this application is followed by the router, so the
 * page keeps its state; any other product's is a navigation to that product, which authenticates the person
 * again. Nothing is followed that the service did not answer.
 */
function useFollow() {
	const navigate = useNavigate();
	return useCallback(
		(destination: string) => {
			let target: URL;
			try {
				target = new URL(destination, window.location.href);
			} catch {
				return;
			}
			if (target.origin === window.location.origin) navigate(`${target.pathname}${target.search}${target.hash}`);
			else window.location.assign(target.href);
		},
		[navigate]
	);
}

/**
 * One adapter per API client for the life of the component, reading the language at every call: what it
 * learned about the inbox being unavailable survives a change of language
 */
function useAdapter() {
	const api = useApi();
	const { locale } = useI18n();
	const code = useRef(locale.code);
	code.current = locale.code;
	return useMemo(() => new RelayAdapter(api, () => code.current), [api]);
}

/**
 * The header entry of the suite inbox: the shared bell with the unread count and a panel of recent items.
 * It reads through CDN's relay and states "unavailable" when this installation runs without Projects.
 */
export function NotificationBell() {
	const { locale } = useI18n();
	const navigate = useNavigate();
	const follow = useFollow();
	return (
		<NotificationEntry
			adapter={useAdapter()}
			href={Paths.patterns.notifications}
			onOpen={follow}
			onView={event => {
				event.preventDefault();
				navigate(Paths.patterns.notifications);
			}}
			products={productNames}
			locale={locale.code}
			interval={60000}
			labels={useLabels()}
		/>
	);
}

/**
 * The full inbox inside the page, with its filter kept in the address
 */
export function Inbox({ state, onState }: { state: { state?: 'unread' | 'all'; product?: string }; onState: (next: { state: 'unread' | 'all'; product: string }) => void }) {
	const { locale } = useI18n();
	return <NotificationInbox adapter={useAdapter()} onOpen={useFollow()} products={PRODUCTS} locale={locale.code} state={state} onState={onState} labels={useLabels()} />;
}
